import React, { useEffect, useState } from "react";

/**
 * SplashScreen — dark brand intro shown while auth resolves (or when the PWA
 * opens from the installed icon). Minimum visible duration: 900 ms so it feels
 * intentional instead of a flash.
 */
export default function SplashScreen({ onDone }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => onDone?.(), 450);
    }, 1100);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-zinc-950 text-white transition-opacity duration-500 ${leaving ? "opacity-0" : "opacity-100"}`}
      data-testid="splash-screen"
    >
      {/* Subtle radial glow */}
      <div className="pointer-events-none absolute inset-0 opacity-70"
           style={{ background: "radial-gradient(circle at 50% 42%, rgba(52,199,89,0.20), rgba(24,24,27,0) 55%)" }} />
      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='3'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative flex flex-col items-center">
        <div className="splash-icon-ring">
          <svg
            width="72"
            height="72"
            viewBox="0 0 192 192"
            aria-hidden="true"
            className="relative z-10"
          >
            <circle cx="96" cy="96" r="62" fill="none" stroke="#FAFAFA" strokeWidth="8" />
            <path d="M96 58 V96 L122 112" stroke="#FAFAFA" strokeWidth="8" strokeLinecap="round" fill="none" />
            <path
              d="M130 62 C152 70 152 106 130 118 C118 112 114 98 114 88 C114 78 118 68 130 62 Z"
              fill="#34C759"
              stroke="#0E3A1D"
              strokeWidth="4"
              strokeLinejoin="round"
            />
            <path d="M128 72 C124 82 124 96 128 110" stroke="#0E3A1D" strokeWidth="3" fill="none" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className="mt-8 font-heading text-7xl font-medium tracking-tight splash-word" data-testid="splash-wordmark">
          Niper
        </h1>
        <div className="mt-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.45em] text-zinc-400 splash-tagline">
          <span className="block h-px w-8 bg-zinc-700" />
          <span>Nihil Perditum</span>
          <span className="block h-px w-8 bg-zinc-700" />
        </div>
        <p className="mt-6 text-sm text-zinc-500 splash-translation italic">nulla è perduto.</p>
      </div>

      <div className="absolute bottom-10 text-[10px] tracking-[0.25em] uppercase text-zinc-600">
        Fresh · Shared · Honest
      </div>

      <style>{`
        @keyframes splashFadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes splashPulse {
          0% { box-shadow: 0 0 0 0 rgba(52,199,89,0.55); }
          70% { box-shadow: 0 0 0 22px rgba(52,199,89,0); }
          100% { box-shadow: 0 0 0 0 rgba(52,199,89,0); }
        }
        @keyframes splashLetter { from { opacity: 0; transform: translateY(14px); letter-spacing: -.04em; } to { opacity: 1; transform: translateY(0); letter-spacing: -.02em; } }
        .splash-icon-ring { display: inline-flex; padding: 14px; border-radius: 9999px; background: radial-gradient(circle, rgba(52,199,89,0.18), rgba(0,0,0,0)); animation: splashPulse 1.6s ease-out 1; }
        .splash-word { animation: splashLetter .7s cubic-bezier(.2,.8,.2,1) both; }
        .splash-tagline { animation: splashFadeUp .8s ease-out .25s both; }
        .splash-translation { animation: splashFadeUp .8s ease-out .45s both; }
      `}</style>
    </div>
  );
}
